import { getCurrentInstance } from "@vue/composition-api";
import { forEach } from "lodash";

/**
 * Returns ability result if ACL is configured or else just return true
 * Useful if you don't know if ACL is configured or not
 * Used in @core files to handle absence of ACL without errors
 * @param {String} action CASL Actions // https://casl.js.org/v4/en/guide/intro#basics
 * @param {String} subject CASL Subject // https://casl.js.org/v4/en/guide/intro#basics
 */
export const can = (action, subject) => {
  const vm = getCurrentInstance().proxy;
  return vm.$can ? vm.$can(action, subject) : true;
};

// Returns true if the item has at least one visible child.
// A child is considered visible if the user "can" perform `action` on `resource`,
// ignoring entries where resource === 'Home'.
export const hasAnyVisibleChildFunc = (item) => {
  // Guard: no children to evaluate
  if (!item || !Array.isArray(item.children) || item.children.length === 0) return false;

  // Helper: checks visibility by the node's own permission (non-recursive)
  const isSelfVisible = (node) =>
    !!node &&
    node.resource &&
    node.resource !== 'Home' &&
    can(node.action || 'read', node.resource);

  // First pass: check only immediate children (level 1).
  //    This matches the original intent and short-circuits early.
  if (item.children.some(isSelfVisible)) return true;

  // Deep pass: check grandchildren and deeper (any depth).
  //    Show the group if any descendant is visible.
  const hasVisibleDescendant = (node) =>
    Array.isArray(node?.children) &&
    node.children.some((child) => isSelfVisible(child) || hasVisibleDescendant(child));

  return item.children.some(hasVisibleDescendant);
};

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewVerticalNavMenuLink = (item) =>{
  // console.log("canViewVerticalNavMenuLink", item.title, item.route, can(item.action, item.resource));
  return can(item.action, item.resource);
}

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource & Hide group if all of it's children are hidden
 * @param {Object} item navigation object item
 */
// eslint-disable-next-line arrow-body-style
export const canViewVerticalNavMenuGroup = (item) => {
  // console.log("canViewVerticalNavMenuGroup", item.title, item.route, can(item.action, item.resource));

  // if (item.header == "safr_events") debugger

  let hasAnyVisibleChild = hasAnyVisibleChildFunc(item);

  // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child
  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild;
  }
  return can(item.action, item.resource) && hasAnyVisibleChild;
};

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewVerticalNavMenuHeader = (item) => {
  // console.log("canViewVerticalNavMenuHeader", item.title, item.route, can(item.action, item.resource));
  return can(item.action, item.resource);
}

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewHorizontalNavMenuLink = (item) => {
  // console.log("canViewHorizontalNavMenuLink", item.title, item.route, can(item.action, item.resource));

  return can(item.action, item.resource);
};
/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewHorizontalNavMenuHeaderLink = (item) => {
  // console.log("canViewHorizontalNavMenuHeaderLink", item.title, item.route);

  // eslint-disable-next-line arrow-body-style
  const hasAnyVisibleChild = item.children.some((grpOrItem) => {
    // If it have children => It's grp
    // Call ACL function based on grp/link
    return grpOrItem.children
      ? canViewHorizontalNavMenuGroup(grpOrItem)
      : canViewHorizontalNavMenuLink(grpOrItem);
  });

  return can(item.action, item.resource) && hasAnyVisibleChild;
};

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource & Hide group if all of it's children are hidden
 * @param {Object} item navigation object item
 */
// eslint-disable-next-line arrow-body-style
export const canViewHorizontalNavMenuGroup = (item) => {
  // console.log("canViewHorizontalNavMenuGroup", item.title, item.route);

  // ? Same logic as canViewVerticalNavMenuGroup
  const hasAnyVisibleChild = item.children.some((i) => can(i.action, i.resource));

  // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child
  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild;
  }
  return can(item.action, item.resource) && hasAnyVisibleChild;
};

// eslint-disable-next-line arrow-body-style
export const canViewHorizontalNavMenuHeaderGroup = (item) => {
  // ? Same logic as canViewVerticalNavMenuGroup but with extra content
  // console.log("canViewHorizontalNavMenuHeaderGroup", item.title, item.route);
  // eslint-disable-next-line arrow-body-style
  const hasAnyVisibleChild = item.children.some((grpOrItem) => {
    // If it have children => It's grp
    // Call ACL function based on grp/link
    return grpOrItem.children
      ? canViewHorizontalNavMenuGroup(grpOrItem)
      : canViewHorizontalNavMenuLink(grpOrItem);
  });

  // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child
  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild;
  }
  return can(item.action, item.resource) && hasAnyVisibleChild;
};
